import {
  FieldError,
  InternalNamePath,
  Rule,
  RuleObject,
  Store,
} from 'rc-field-form/lib/interface'
import { FormStore } from 'rc-field-form/lib/useForm'
import { HOOK_MARK } from 'rc-field-form/lib/FieldContext'
import _get from 'lodash/get'
import { validateRules } from 'rc-field-form/lib/utils/validateUtil'
// Validation messages are emitted as i18next message keys + params, never
// rendered strings. The frontend renders them via i18next at display time; the
// backend passes the key + params through as a JSON "code" (no server-side
// render, so no caller-locale is needed and no German copy is produced
// server-side). Numeric/date values interpolated into a message are formatted
// here with Intl so the param carries a ready-to-insert value.

type Unpacked<T> = T extends (infer U)[] ? U : T

export type RuleDefinition<Type> = {
  [Property in keyof Type]:
    | RuleDefinition<Type[Property]>
    | Rule[]
    | ((
        values: Type[Property]
      ) => (RuleDefinition<Unpacked<Type[Property]>> | Rule[])[])
}

/** A reference to a catalog message: an i18next key plus interpolation params. */
export type ValidationMessage = {
  key: string
  params?: Record<string, string | number>
}

export type ValidationI18n = {
  _: (msg: ValidationMessage) => string
  date(value: string | Date, format?: Intl.DateTimeFormatOptions): string
  number(value: number, format?: Intl.NumberFormatOptions): string
}

// Values interpolated into validation messages keep their current German
// formatting; locale-aware formatting is handled separately (CL-1497).
const VALIDATION_LOCALE = 'de-DE'

// Date-only `YYYY-MM-DD` strings parse as UTC midnight via `new Date(str)`,
// which can render the previous day under `Intl` in UTC-negative zones. Parse
// those as local midnight instead; other strings keep the native parse.
export const toDisplayDate = (value: string | Date): Date =>
  typeof value === 'string'
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value)
    : value

const formatDate = (
  value: string | Date,
  format?: Intl.DateTimeFormatOptions
): string =>
  new Intl.DateTimeFormat(VALIDATION_LOCALE, format).format(
    toDisplayDate(value)
  )

const formatNumber = (
  value: number,
  format?: Intl.NumberFormatOptions
): string => new Intl.NumberFormat(VALIDATION_LOCALE, format).format(value)

// Both the frontend and the backend run the shared rules through this
// passthrough: rules emit `{ key, params }`, serialized to a JSON code that the
// frontend renders via i18next. The backend never renders. The two names are
// kept for existing call sites; they behave identically now.
export const JSON_I18N: ValidationI18n = {
  _: (msg: ValidationMessage) => JSON.stringify(msg),
  date: formatDate,
  number: formatNumber,
}

export const DEFAULT_I18N: ValidationI18n = JSON_I18N

export function validate<T extends Store>(
  values: T,
  rules: RuleDefinition<T>
): Promise<void> {
  return new Promise((resolve, reject) => {
    const formStore = new FormStore(() => {})
    const form = formStore.getForm()

    const hooks = form.getInternalHooks(HOOK_MARK)!

    // Register the fields
    function registerFields(
      rules: RuleDefinition<any> | Rule[],
      parentPath: (string | number)[] = [],
      isList: boolean = false
    ) {
      if (Array.isArray(rules)) {
        // @ts-expect-error Not all fields defined
        hooks.registerField({
          isList: () => isList,
          isListField: () => parentPath.some((p) => typeof p === 'number'),
          getNamePath: () => parentPath,
          props: {
            name: parentPath,
            rules,
          },
        })
      } else {
        Object.keys(rules).forEach((key) => {
          let ruleDefinition = rules[key as keyof RuleDefinition<T>]

          let isListField = false
          const namePath = [...parentPath, key]
          const value = _get(values, namePath) as any[]
          if (typeof ruleDefinition === 'function') {
            const functionRuleDefinition = ruleDefinition as (
              values: any
            ) => (RuleDefinition<any> | Rule[])[]
            // Unwrap lists
            const asd = functionRuleDefinition(value)
            asd.forEach((v, i) => {
              registerFields(v, [...parentPath, key, i], true)
            })
            return
          }

          if (
            typeof ruleDefinition === 'object' &&
            !Array.isArray(ruleDefinition)
          ) {
            // Unwrap nested objects
            registerFields(
              ruleDefinition as RuleDefinition<any>,
              namePath,
              isListField
            )
            return
          }

          // @ts-expect-error Not all fields defined
          hooks.registerField({
            isList: () => isList,
            isListField: () => parentPath.some((p) => typeof p === 'number'),
            getNamePath: () => namePath,
            onStoreChange: () => {},
            validateRules: async (_options) => {
              const finalRules = (ruleDefinition as Rule[]).map(
                (rule: Rule): RuleObject => {
                  if (typeof rule === 'function') {
                    return rule(form)
                  }
                  return rule
                }
              )

              return await validateRules(
                namePath,
                value,
                finalRules,
                {},
                'parallel'
              )
            },
            props: {
              name: namePath,
              rules: ruleDefinition as Rule[],
            },
          })
        })
      }
    }

    registerFields(rules)

    // Set the form values
    hooks.setInitialValues(values, true)

    form
      .validateFields({
        validateOnly: true,
      })
      .then(async () => {
        // @ts-expect-error access to internal field of formStore
        const messages: FieldError[] = await formStore.lastValidatePromise
        const warningMessages = messages.filter(
          (item) => item.warnings.length > 0
        )

        if (warningMessages.length > 0) {
          reject(new ValidationException(warningMessages))
        }

        resolve()
      })
      .catch((errors) => {
        // Something is invalid
        reject(new ValidationException(errors.errorFields))
      })
  })
}

export class ValidationException extends Error {
  constructor(
    public readonly errorFields: {
      name: InternalNamePath
      errors: string[]
      warnings: string[]
    }[]
  ) {
    super('Validation failed')
  }
}

export class OneTimePaymentValidationException extends ValidationException {
  constructor(
    errorFields: {
      name: InternalNamePath
      errors: string[]
      warnings: string[]
    }[],
    public readonly documentUid: string,
    public readonly yearMonth: string
  ) {
    super(errorFields)
  }
}
